import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { thirteenWeekCashFlowApi } from "../../helpers/thirteenWeekCashFlowApi";
import Chip from "./Chip";
import { dateLabel, dateTimeLabel, monthLabel, money } from "./money";
import CurrencyInput from "./CurrencyInput";

const tabs = [
  ["overview", "Overview"],
  ["mappings", "Mappings"],
  ["billcom", "Bill.com"],
  ["adPlatforms", "Ad platforms"],
  ["poAp", "PO/AP"],
  ["tax", "Sales tax"],
  ["holidays", "Holidays"],
  ["payroll", "Payroll"],
  ["cards", "Cards"],
  ["recurring", "Recurring cash"],
  ["calendar", "Calendar"],
];
const categories = [
  "operating_revenue",
  "other_inflow",
  "sales_tax",
  "ap_operating",
  "ap_inventory",
  "credit_card",
  "payroll",
  "owner_distribution",
  "debt",
  "other_outflow",
];
const categoryLabels = {
  operating_revenue: "Operating revenue",
  other_inflow: "Other cash in",
  sales_tax: "Sales tax",
  ap_operating: "AP – Operating",
  ap_inventory: "AP – Inventory",
  credit_card: "Credit card",
  payroll: "Payroll",
  owner_distribution: "Owner distributions",
  debt: "Debt repay",
  other_outflow: "Other cash out",
};
const frequencies = [
  "once",
  "weekly",
  "biweekly",
  "semi_monthly",
  "monthly",
  "quarterly",
  "annual",
];
const weekdays = [
  ["1", "Monday"],
  ["2", "Tuesday"],
  ["3", "Wednesday"],
  ["4", "Thursday"],
  ["5", "Friday"],
];

const taxRowForm = (initial) => ({
  ...initial,
  amount: initial?.amount ?? "",
  frequency: initial?.frequency || "monthly",
  anchorDate: String(initial?.anchorDate ?? initial?.anchor_date ?? "").slice(0, 10),
  dayOfMonth: initial?.dayOfMonth ?? initial?.day_of_month ?? "",
  businessDayRule:
    initial?.businessDayRule || initial?.business_day_rule || "previous",
  isActive: initial?.isActive ?? initial?.is_active ?? true,
  meta: {
    ...(initial?.meta || {}),
    state: initial?.meta?.state || initial?.meta?.jurisdiction || "",
  },
});

const taxRowValues = (row) => {
  const amount = row?.amount;
  const dayOfMonth = row?.dayOfMonth ?? row?.day_of_month;
  return {
    state: String(row?.meta?.state || row?.meta?.jurisdiction || ""),
    estimate: row?.meta?.estimate ? JSON.stringify(row.meta.estimate) : null,
    amount: amount == null || amount === "" ? null : Number(amount),
    frequency: row?.frequency || "monthly",
    dayOfMonth:
      dayOfMonth == null || dayOfMonth === "" ? null : Number(dayOfMonth),
    businessDayRule:
      row?.businessDayRule || row?.business_day_rule || "previous",
    isActive: (row?.isActive ?? row?.is_active) !== false,
    anchorDate: String(row?.anchorDate ?? row?.anchor_date ?? "").slice(0, 10),
  };
};

const sameTaxRowValues = (left, right) => {
  const leftValues = taxRowValues(left);
  const rightValues = taxRowValues(right);
  return Object.keys(leftValues).every(
    (key) =>
      leftValues[key] === rightValues[key] ||
      (Number.isNaN(leftValues[key]) && Number.isNaN(rightValues[key])),
  );
};

function Panel({ title, children, action }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-lg font-semibold text-gray-900">{title}</p>
        {action}
      </div>
      {children}
    </section>
  );
}

function Empty({ text }) {
  return (
    <div className="mt-2 rounded border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-600">
      {text}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block text-sm text-gray-700 ${className}`}>
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-widest text-gray-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function ScheduleForm({ initial, category, onSave, onCancel, tax = false }) {
  const [form, setForm] = useState({
    id: initial?.id,
    label: initial?.label || "",
    category: initial?.category || category || "other_outflow",
    amount: initial?.amount ?? "",
    frequency: initial?.frequency || "monthly",
    anchorDate: initial?.anchorDate || initial?.anchor_date || "",
    dayOfMonth: initial?.dayOfMonth || initial?.day_of_month || "",
    businessDayRule:
      initial?.businessDayRule || initial?.business_day_rule || "previous",
    startDate: initial?.startDate || initial?.start_date || "",
    endDate: initial?.endDate || initial?.end_date || "",
    isActive: initial?.isActive ?? initial?.is_active ?? true,
    meta: initial?.meta || {},
  });
  const [formError, setFormError] = useState("");
  const update = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const submit = () => {
    if (!form.label.trim()) return setFormError("Label is required");
    if (!form.category) return setFormError("Category is required");
    if (!form.frequency) return setFormError("Frequency is required");
    if (form.amount === "" || !Number.isFinite(Number(form.amount))) {
      return setFormError("Amount is required");
    }
    if (form.anchorDate && !/^\d{4}-\d{2}-\d{2}$/.test(form.anchorDate)) {
      return setFormError("Anchor date must be a valid date");
    }
    setFormError("");
    onSave({
      ...form,
      amount: Number(form.amount),
      dayOfMonth: form.dayOfMonth === "" ? null : form.dayOfMonth,
      anchorDate: form.anchorDate || null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      category: category || form.category,
    });
  };
  return (
    <div className="grid gap-2 rounded-md border border-gray-100 bg-gray-50 p-3 md:grid-cols-4">
      <Field label="Label">
        <input
          value={form.label}
          onChange={(event) => update("label", event.target.value)}
          className="h-8 w-full rounded border-gray-200 text-sm"
        />
      </Field>
      {!category && (
        <Field label="Category">
          <select
            value={form.category}
            onChange={(event) => update("category", event.target.value)}
            className="h-8 w-full rounded border-gray-200 text-sm"
          >
            {categories.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Amount">
        <CurrencyInput
          value={form.amount}
          onChange={(value) =>
            update(
              "amount",
              value,
            )
          }
          className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
        />
      </Field>
      <Field label="Frequency">
        <select
          value={form.frequency}
          onChange={(event) => update("frequency", event.target.value)}
          className="h-8 w-full rounded border-gray-200 text-sm"
        >
          {(tax
            ? ["monthly", "quarterly", "annual", "semi_monthly"]
            : frequencies
          ).map((value) => (
            <option key={value} value={value}>
              {value.replace("_", " ")}
            </option>
          ))}
        </select>
      </Field>
      {!tax && <Field label="Anchor date">
        <input
          type="date"
          value={String(form.anchorDate || "").slice(0, 10)}
          onChange={(event) => update("anchorDate", event.target.value)}
          className="h-8 w-full rounded border-gray-200 text-sm"
        />
      </Field>}
      <Field label={tax ? "Due day" : "Day of month"}>
        <input
          type="number"
          min="1"
          max="31"
          value={form.dayOfMonth}
          onChange={(event) =>
            update(
              "dayOfMonth",
              event.target.value ? Number(event.target.value) : null,
            )
          }
          className="h-8 w-full rounded border-gray-200 text-right"
        />
      </Field>
      <Field label="Business day">
        <select
          value={form.businessDayRule}
          onChange={(event) => update("businessDayRule", event.target.value)}
          className="h-8 w-full rounded border-gray-200 text-sm"
        >
          <option value="none">None</option>
          <option value="previous">Previous</option>
          <option value="next">Next</option>
        </select>
      </Field>
      {!tax && <Field label="Start date">
        <input
          type="date"
          value={String(form.startDate || "").slice(0, 10)}
          onChange={(event) => update("startDate", event.target.value || null)}
          className="h-8 w-full rounded border-gray-200 text-sm"
        />
      </Field>}
      {!tax && <Field label="End date">
        <input
          type="date"
          value={String(form.endDate || "").slice(0, 10)}
          onChange={(event) => update("endDate", event.target.value || null)}
          className="h-8 w-full rounded border-gray-200 text-sm"
        />
      </Field>}
      {tax && (
        <>
          <Field label="State">
            <input
              value={form.meta.state || form.meta.jurisdiction || ""}
              onChange={(event) =>
                update("meta", {
                  ...form.meta,
                  state: event.target.value,
                })
              }
              className="h-8 w-full rounded border-gray-200 text-sm"
            />
          </Field>
          <Field label="Filing frequency">
            <select
              value={form.meta.filingFrequency || ""}
              onChange={(event) =>
                update("meta", {
                  ...form.meta,
                  filingFrequency: event.target.value,
                })
              }
              className="h-8 w-full rounded border-gray-200 text-sm"
            >
              <option value="">Select</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="annual">Annual</option>
              <option value="semi_monthly">Semi-monthly</option>
            </select>
          </Field>
        </>
      )}
      <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(event) => update("isActive", event.target.checked)}
        />{" "}
        Active
      </label>
      <div className="flex items-end gap-2 md:col-span-4">
        <button
          type="button"
          onClick={submit}
          className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
        >
          Save
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="h-8 rounded border border-gray-200 px-3 text-xs text-gray-700"
          >
            Cancel
          </button>
        )}
      </div>
      {formError && (
        <p className="md:col-span-4 text-xs text-red-700">{formError}</p>
      )}
    </div>
  );
}

function TaxRowEditor({ initial, onSave, onDelete, onDirtyChange }) {
  const [form, setForm] = useState(() => taxRowForm(initial));
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const isNew = String(form.id || initial?.id || "").startsWith("new-");
  const dirty = isNew || !sameTaxRowValues(form, initial);
  const rowId = String(initial?.id ?? form.id ?? "");
  const update = (key, value) =>
    setForm((current) => ({ ...current, [key]: value }));
  const updateMeta = (key, value) =>
    setForm((current) => ({
      ...current,
      meta: { ...current.meta, [key]: value },
    }));
  useEffect(() => {
    if (!savedSnapshot || initial === savedSnapshot.initial) {
      return;
    }
    setForm((current) =>
      sameTaxRowValues(current, savedSnapshot.form)
        ? taxRowForm(initial)
        : current,
    );
    setSavedSnapshot(null);
  }, [initial, savedSnapshot]);
  useEffect(() => {
    if (!rowId) return undefined;
    onDirtyChange(rowId, dirty);
    return () => onDirtyChange(rowId, false);
  }, [dirty, onDirtyChange, rowId]);
  const save = async () => {
    const meta = { ...form.meta, state: form.meta.state };
    let label = form.label || `Sales tax ${form.meta.state || "schedule"}`;
    if (
      meta.estimate
      && Number(form.amount) !== Number(meta.estimate.amount)
    ) {
      delete meta.estimate;
      label = label.replace(/ \(temporary estimate\)$/i, "");
    }
    const saved = await onSave(
      {
        ...(String(form.id || "").startsWith("new-") ? {} : { id: form.id }),
        ...form,
        ...(String(form.id || "").startsWith("new-") ? { id: undefined } : {}),
        label,
        category: "sales_tax",
        amount: Number(form.amount || 0),
        anchorDate: form.anchorDate || null,
        startDate: null,
        endDate: null,
        meta,
      },
      { draftId: isNew ? form.id : null },
    );
    if (saved && !isNew) {
      setSavedSnapshot({
        initial,
        form,
      });
    }
  };
  return (
    <tr className="border-t border-gray-100">
      <td className="px-2 py-2">
        <div>
          <input
            value={form.meta.state}
            onChange={(event) => updateMeta("state", event.target.value)}
            className="h-8 w-24 rounded border-gray-200 text-sm"
            placeholder="State"
          />
          {form.meta.estimate && (
            <p
              className="mt-1 text-xs font-medium text-amber-700"
              title={form.meta.estimate.note}
            >
              Temporary estimate
            </p>
          )}
        </div>
      </td>
      <td className="px-2 py-2">
        <CurrencyInput
          value={form.amount ?? ""}
          onChange={(value) => update("amount", value)}
          className="h-8 w-28 rounded border-gray-200 text-right text-sm"
        />
      </td>
      <td className="px-2 py-2">
        <select
          value={form.frequency || "monthly"}
          onChange={(event) => update("frequency", event.target.value)}
          className="h-8 rounded border-gray-200 text-sm"
        >
          {["monthly", "quarterly", "annual", "semi_monthly"].map((value) => (
            <option key={value} value={value}>
              {value.replace("_", " ")}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-2">
        {["quarterly", "annual"].includes(form.frequency || "monthly") ? (
          <input
            type="date"
            value={String(form.anchorDate || "").slice(0, 10)}
            onChange={(event) => update("anchorDate", event.target.value)}
            className="h-8 w-36 rounded border-gray-200 text-sm"
          />
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="px-2 py-2">
        <input
          type="number"
          min="1"
          max="31"
          value={form.dayOfMonth ?? ""}
          onChange={(event) =>
            update(
              "dayOfMonth",
              event.target.value ? Number(event.target.value) : null,
            )
          }
          className="h-8 w-20 rounded border-gray-200 text-right text-sm"
        />
      </td>
      <td className="px-2 py-2">
        <select
          value={form.businessDayRule}
          onChange={(event) => update("businessDayRule", event.target.value)}
          className="h-8 rounded border-gray-200 text-sm"
        >
          <option value="none">None</option>
          <option value="previous">Previous</option>
          <option value="next">Next</option>
        </select>
      </td>
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          checked={form.isActive !== false}
          onChange={(event) => update("isActive", event.target.checked)}
        />
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="text-xs font-medium text-amber-700">Unsaved</span>
          )}
          <button
            type="button"
            onClick={save}
            className={
              dirty
                ? "h-8 rounded bg-black px-3 text-xs font-medium text-white"
                : "h-8 rounded border border-gray-200 px-3 text-xs font-medium text-gray-700"
            }
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => onDelete(form)}
            className="text-xs text-red-700"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function ConfigurationTab({
  settings,
  schedules,
  rules,
  holidays,
  cards,
  summary,
  builtinMappings = [],
  loading,
  error,
  onImport,
  onRefresh,
}) {
  const [tab, setTab] = useState("overview");
  const [draft, setDraft] = useState(settings?.settings || settings || {});
  const [ruleForm, setRuleForm] = useState({
    pattern: "",
    category: "other_outflow",
    priority: 100,
  });
  const [mappingSearch, setMappingSearch] = useState("");
  const [showBuiltin, setShowBuiltin] = useState(false);
  const [scheduleForm, setScheduleForm] = useState(null);
  const [holidayForm, setHolidayForm] = useState({ date: "", label: "" });
  const [holidayError, setHolidayError] = useState("");
  const [calendarError, setCalendarError] = useState("");
  const [importKind, setImportKind] = useState("sales-tax");
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");
  const [billcomSearch, setBillcomSearch] = useState("");
  const [billcomVendors, setBillcomVendors] = useState([]);
  const [billcomSelectedVendors, setBillcomSelectedVendors] = useState([]);
  const [billcomPage, setBillcomPage] = useState(1);
  const [billcomTotal, setBillcomTotal] = useState(0);
  const [showInactiveCards, setShowInactiveCards] = useState(false);
  const [taxDraftRows, setTaxDraftRows] = useState([]);
  const [dirtyTaxRowIds, setDirtyTaxRowIds] = useState(() => new Set());
  const [billcomLoading, setBillcomLoading] = useState(false);
  const [billcomError, setBillcomError] = useState("");
  const onTaxDirtyChange = useCallback((id, isDirty) => {
    const rowId = String(id ?? "");
    if (!rowId) return;
    setDirtyTaxRowIds((current) => {
      if (current.has(rowId) === isDirty) return current;
      const next = new Set(current);
      if (isDirty) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  }, []);
  useEffect(() => {
    if (!dirtyTaxRowIds.size || typeof window === "undefined") {
      return undefined;
    }
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirtyTaxRowIds.size]);
  useEffect(() => setDraft(settings?.settings || settings || {}), [settings]);
  useEffect(() => {
    if (tab !== "billcom") return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setBillcomLoading(true);
      setBillcomError("");
      try {
        const response = await thirteenWeekCashFlowApi.getBillcomVendors({
          search: billcomSearch,
          page: billcomPage,
          pageSize: 25,
          ids: (draft?.apVendors?.inventoryVendorIds || []).slice(0, 100).join(","),
        });
        if (!cancelled) {
          const result = response?.data || response || {};
          setBillcomVendors(result.vendors || []);
          setBillcomTotal(Number(result.total || 0));
        }
      } catch (requestError) {
        if (!cancelled) {
          setBillcomError(
            requestError?.response?.data?.error ||
              requestError?.message ||
              "Vendors could not be loaded",
          );
        }
      } finally {
        if (!cancelled) setBillcomLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [billcomPage, billcomSearch, draft, tab]);
  const refresh = () => onRefresh();
  const save = async (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    try {
      await thirteenWeekCashFlowApi.updateSetting(key, value);
      toast.success("Configuration saved", { position: "bottom-right" });
      refresh();
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.error ||
          "Configuration could not be saved",
        { position: "bottom-right" },
      );
    }
  };
  const mutate = async (request, message = "Configuration saved") => {
    try {
      await request();
      toast.success(message, { position: "bottom-right" });
      refresh();
      return true;
    } catch (requestError) {
      toast.error(
        requestError?.response?.data?.error ||
          "Configuration could not be saved",
        { position: "bottom-right" },
      );
      return false;
    }
  };
  const setting = (key) => draft[key] || {};
  const alerts = summary?.alerts || [];
  const stale = alerts.find((alert) => alert.code === "stale_import");
  const missingForecast = alerts.find(
    (alert) => alert.code === "missing_forecast_source",
  );
  const coverage = [
    [
      "Plaid cash accounts",
      summary?.openingCash?.asOf
        ? dateTimeLabel(summary.openingCash.asOf)
        : "No import",
      stale ? "Stale" : "Live",
      stale ? "warning" : "success",
    ],
    [
      "Effective Capital",
      money(summary?.effectiveCapital?.kingsLoot),
      `Floor ${money(summary?.effectiveCapital?.floor)}`,
      "info",
    ],
    [
      "Forecast targets",
      missingForecast
        ? `Missing: ${(missingForecast.meta?.months || []).join(", ")}`
        : "Configured",
      missingForecast ? "Missing" : "Live",
      missingForecast ? "warning" : "success",
    ],
    [
      "PO/AP due",
      setting("apInventory").mode || "manual",
      setting("apInventory").mode === "po_auto" ? "Auto" : "Manual",
      "info",
    ],
    [
      `Category mappings: ${(rules || []).filter((rule) => rule.isActive !== false).length} active rules + ${builtinMappings.length} built-in`,
      "View mappings",
      "info",
    ],
  ];
  const mappingCoverageLabel = "Category mappings:";
  const inventoryVendorIds = useMemo(
    () => draft?.apVendors?.inventoryVendorIds || [],
    [draft],
  );
  const inventoryVendorKey = inventoryVendorIds.join(",");
  useEffect(() => {
    if (tab !== "billcom" || !inventoryVendorIds.length) return undefined;
    let cancelled = false;
    thirteenWeekCashFlowApi
      .getBillcomVendors({
        ids: inventoryVendorIds.slice(0, 100).join(","),
        page: 1,
        pageSize: 1,
      })
      .then((response) => {
        if (!cancelled) {
          const result = response?.data || response || {};
          setBillcomSelectedVendors(result.vendors || []);
        }
      })
      .catch(() => {
        if (!cancelled) setBillcomSelectedVendors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [inventoryVendorKey, inventoryVendorIds, tab]);

  if (loading && !settings)
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        Loading configuration…
      </div>
    );
  if (error && !settings)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {error}
      </div>
    );
  if (!settings)
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-600">
        No configuration is available.
      </div>
    );

  const addRule = () =>
    mutate(
      () =>
        (ruleForm.id
          ? thirteenWeekCashFlowApi.updateCategoryRule(ruleForm)
          : thirteenWeekCashFlowApi.createCategoryRule(ruleForm)
        ).then(() =>
          setRuleForm({
            pattern: "",
            category: "other_outflow",
            priority: 100,
          }),
        ),
      "Category rule saved",
    );
  const saveSchedule = (payload) =>
    mutate(
      () =>
        (payload.id
          ? thirteenWeekCashFlowApi.updateSchedule(payload)
          : thirteenWeekCashFlowApi.createSchedule(payload)
        ).then(() => setScheduleForm(null)),
      "Schedule saved",
    );
  const saveTaxRow = (payload, { draftId } = {}) =>
    mutate(
      () =>
        (payload.id
          ? thirteenWeekCashFlowApi.updateSchedule(payload)
          : thirteenWeekCashFlowApi.createSchedule(payload)
        ).then(() =>
          setTaxDraftRows((rows) =>
            rows.filter((row) => {
              if (draftId) return row.id !== draftId;
              return payload.id
                ? row.id !== payload.id
                : row.meta?.state !== payload.meta?.state;
            }),
          ),
        ),
      "Sales-tax schedule saved",
    );
  const deleteTaxRow = (payload) => {
    if (!payload.id || String(payload.id).startsWith("new-")) {
      setTaxDraftRows((rows) => rows.filter((row) => row.id !== payload.id));
      return;
    }
    mutate(
      () => thirteenWeekCashFlowApi.deleteSchedule(payload.id),
      "Sales-tax schedule deleted",
    );
  };
  const saveHoliday = () =>
    (() => {
      if (!holidayForm.date || !holidayForm.label.trim()) {
        setHolidayError("Holiday date and label are required");
        return null;
      }
      setHolidayError("");
      return mutate(
        () =>
          thirteenWeekCashFlowApi
            .createHoliday({
              date: holidayForm.date || null,
              label: holidayForm.label.trim(),
            })
            .then(() => setHolidayForm({ date: "", label: "" })),
        "Holiday saved",
      );
    })();
  const saveCalendar = () => {
    setCalendarError("");
    save("revenue", setting("revenue"));
  };
  const importFileData = async () => {
    if (!importFile) {
      setImportError("Choose a CSV file first.");
      return;
    }
    setImportError("");
    try {
      const result = await onImport({
        kind: importKind,
        csv: await importFile.text(),
        filename: importFile.name,
      });
      setImportResult(result);
      setImportFile(null);
      onRefresh();
    } catch (requestError) {
      setImportError(
        (typeof requestError === "string" && requestError) ||
          requestError?.response?.data?.error ||
          requestError?.message ||
          "Import could not be completed",
      );
    }
  };
  const updateRevenue = (key, value) =>
    setDraft((current) => ({
      ...current,
      revenue: { ...setting("revenue"), [key]: value },
    }));
  const revenue = setting("revenue");
  const weekdayWeights = revenue.weekdayWeights || {};
  const weekdayWeightSum = ["mon", "tue", "wed", "thu", "fri"].reduce(
    (sum, key) => sum + Number(weekdayWeights[key] || 0),
    0,
  );
  const updateCardOverride = (card, key, value) =>
    setDraft((current) => ({
      ...current,
      creditCards: {
        ...setting("creditCards"),
        overrides: {
          ...setting("creditCards").overrides,
          [card.plaidAccountId]: {
            ...(setting("creditCards").overrides?.[card.plaidAccountId] || {}),
            [key]: value,
          },
        },
      },
    }));
  const updateCardDate = (card, month, value) =>
    setDraft((current) => {
      const currentOverride =
        setting("creditCards").overrides?.[card.plaidAccountId] || {};
      const dates = { ...(currentOverride.dates || {}) };
      if (value) dates[month] = value;
      else delete dates[month];
      return {
        ...current,
        creditCards: {
          ...setting("creditCards"),
          overrides: {
            ...setting("creditCards").overrides,
            [card.plaidAccountId]: { ...currentOverride, dates },
          },
        },
      };
    });
  const inactiveCards = (cards || []).filter(
    (card) =>
      setting("creditCards").overrides?.[card.plaidAccountId]?.enabled === false,
  );
  const activeCards = (cards || []).filter(
    (card) =>
      setting("creditCards").overrides?.[card.plaidAccountId]?.enabled !== false,
  );
  const toggleInventoryVendor = (vendorId, checked) => {
    const ids = new Set(inventoryVendorIds.map(String));
    if (checked) ids.add(String(vendorId));
    else ids.delete(String(vendorId));
    setDraft((current) => ({
      ...current,
      apVendors: { ...setting("apVendors"), inventoryVendorIds: [...ids] },
    }));
  };
  const updateAdPlatform = (platform, key, value) => {
    setDraft((current) => ({
      ...current,
      adPlatforms: {
        ...current.adPlatforms,
        [platform]: {
          ...setting("adPlatforms")[platform],
          [key]: value,
        },
      },
    }));
  };
  const updateAdOverride = (platform, monthKey, value) => {
    const overrides = {
      ...(setting("adPlatforms")[platform]?.overrides || {}),
    };
    if (value === "") delete overrides[monthKey];
    else overrides[monthKey] = Number(value);
    updateAdPlatform(platform, "overrides", overrides);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-white p-2">
        {tabs.map(([key, label]) => (
          <button
            type="button"
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === key ? "bg-black text-white" : "text-gray-600 hover:bg-gray-100"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "overview" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Liquidity thresholds">
            <div className="space-y-3">
              {[
                ["grossCashFloor", "Gross cash floor"],
                ["cautionThreshold", "Caution threshold"],
                ["effectiveCapitalFloor", "Effective Capital floor"],
              ].map(([key, label]) => (
                <label
                  key={key}
                  className="flex items-center justify-between gap-4 text-sm text-gray-700"
                >
                  <span>{label}</span>
                  <CurrencyInput
                    value={setting("thresholds")[key] || 0}
                    onChange={(value) =>
                      setDraft((current) => ({
                        ...current,
                        thresholds: {
                          ...setting("thresholds"),
                          [key]: value,
                        },
                      }))
                    }
                    onBlur={() => save("thresholds", setting("thresholds"))}
                    className="h-8 w-36 rounded-md border-gray-200 text-right tabular-nums"
                  />
                </label>
              ))}
            </div>
          </Panel>
          <Panel title="Source coverage">
            <div className="space-y-2 text-sm">
              {coverage.map(([label, value, state, tone]) => (
                <button
                  type="button"
                  key={label}
                  onClick={() =>
                    label.startsWith(mappingCoverageLabel) && setTab("mappings")
                  }
                  className="flex items-start justify-between gap-3 border-b border-gray-100 py-2 last:border-0"
                >
                  <div>
                    <p className="font-medium text-gray-700">{label}</p>
                    <p className="text-xs text-gray-500">{value}</p>
                  </div>
                  <Chip tone={tone}>{state}</Chip>
                </button>
              ))}
            </div>
          </Panel>
          <Panel title="Visible categories">
            <div className="grid gap-2 sm:grid-cols-2">
              {categories.map((category) => {
                const hidden =
                  setting("display").hiddenCategories?.includes(category);
                return (
                  <label
                    key={category}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={!hidden}
                      onChange={(event) => {
                        const current = new Set(
                          setting("display").hiddenCategories || [],
                        );
                        if (event.target.checked) current.delete(category);
                        else current.add(category);
                        save("display", {
                          ...setting("display"),
                          hiddenCategories: [...current],
                        });
                      }}
                    />
                    {categoryLabels[category] || category}
                  </label>
                );
              })}
            </div>
          </Panel>
          <Panel title="Import data">
            <div className="grid gap-3 md:grid-cols-[1fr_2fr_auto]">
              <Field label="Import type">
                <select
                  value={importKind}
                  onChange={(event) => setImportKind(event.target.value)}
                  className="h-8 w-full rounded border-gray-200 text-sm"
                >
                  <option value="sales-tax">Sales tax</option>
                  <option value="holidays">Holidays</option>
                  <option value="category-mappings">Category mappings</option>
                  <option value="amazon-feed">Amazon feed</option>
                </select>
              </Field>
              <Field label="CSV file">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) =>
                    setImportFile(event.target.files?.[0] || null)
                  }
                  className="block h-8 w-full rounded border border-gray-200 bg-white px-2 py-1 text-xs"
                />
              </Field>
              <button
                type="button"
                onClick={importFileData}
                disabled={!importFile}
                className="h-8 self-end rounded bg-black px-3 text-xs font-medium text-white disabled:opacity-40"
              >
                Import
              </button>
            </div>
            {importError && (
              <p className="mt-2 text-xs text-red-700">{importError}</p>
            )}
            {importResult && (
              <p className="mt-2 text-xs text-gray-600">
                Created {importResult.created}, updated {importResult.updated},
                skipped {importResult.skipped?.length || 0}.
                {importResult.skipped?.length
                  ? ` ${importResult.skipped.map((item) => `Row ${item.row}: ${item.reason}`).join("; ")}`
                  : ""}
              </p>
            )}
          </Panel>
        </div>
      )}
      {tab === "billcom" && (
        <Panel
          title="Bill.com vendors → AP Inventory"
          action={
            <button
              type="button"
              onClick={() => save("apVendors", setting("apVendors"))}
              className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
            >
              Save
            </button>
          }
        >
          <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
            <Field label="Search vendors" className="min-w-[240px] flex-1">
              <input
                value={billcomSearch}
                onChange={(event) => setBillcomSearch(event.target.value)}
                placeholder="Search name or vendor ID"
                className="h-8 w-full rounded border-gray-200 text-sm"
              />
            </Field>
            <p className="text-xs text-gray-500">
              {inventoryVendorIds.length} vendors marked inventory. Category rules
              can classify an exactly matched owner-distribution vendor; other
              vendors are AP Operating.
            </p>
            <p className="text-xs text-gray-500">{billcomTotal} matching vendors</p>
          </div>
          {billcomError && (
            <p className="mb-2 text-xs text-red-700">{billcomError}</p>
          )}
          {inventoryVendorIds.length > 0 && (
            <div className="mb-3 rounded border border-blue-100 bg-blue-50/40 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-blue-700">
                Inventory vendors
              </p>
              <div className="flex flex-wrap gap-2">
                {inventoryVendorIds.map((vendorId) => {
                  const vendor = [...billcomVendors, ...billcomSelectedVendors].find(
                    (item) => String(item.vendorId) === String(vendorId),
                  );
                  return (
                    <label
                      key={vendorId}
                      className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-xs text-gray-700"
                    >
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleInventoryVendor(vendorId, false)}
                      />
                      {vendor?.name || vendorId}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
            <button
              type="button"
              disabled={billcomPage <= 1}
              onClick={() => setBillcomPage((page) => Math.max(1, page - 1))}
              className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
            >
              Previous
            </button>
            <span>Page {billcomPage}</span>
            <button
              type="button"
              disabled={billcomPage * 25 >= billcomTotal}
              onClick={() => setBillcomPage((page) => page + 1)}
              className="rounded border border-gray-200 px-2 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
          {billcomLoading ? (
            <p className="text-sm text-gray-500">Loading vendors…</p>
          ) : (
            <div className="overflow-x-auto rounded border border-gray-100">
              <table className="w-full text-left text-xs">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-400">
                  <tr>
                    <th className="px-3 py-2">AP Inventory</th>
                    <th className="px-3 py-2">Vendor name</th>
                    <th className="px-3 py-2">Vendor ID</th>
                    <th className="px-3 py-2">Paid last 12 months</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {billcomVendors.map((vendor) => (
                    <tr
                      key={vendor.vendorId}
                      className="border-t border-gray-100"
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={inventoryVendorIds
                            .map(String)
                            .includes(String(vendor.vendorId))}
                          onChange={(event) =>
                            toggleInventoryVendor(
                              vendor.vendorId,
                              event.target.checked,
                            )
                          }
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-gray-800">
                        {vendor.name || "—"}
                      </td>
                      <td className="px-3 py-2 font-mono text-gray-500">
                        {vendor.vendorId}
                      </td>
                      <td className="px-3 py-2 tabular-nums">
                        {money(vendor.paidLast12Months)}
                      </td>
                      <td className="px-3 py-2">
                        {vendor.archived ? (
                          <Chip tone="warning">Archived</Chip>
                        ) : (
                          <Chip tone="success">Active</Chip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!billcomVendors.length && (
                <p className="px-3 py-4 text-sm text-gray-500">
                  No Bill.com vendors found.
                </p>
              )}
            </div>
          )}
        </Panel>
      )}
      {tab === "adPlatforms" && (
        <Panel
          title="Ad platform payments (Meta / Google)"
          action={
            <button
              type="button"
              onClick={() => save("adPlatforms", setting("adPlatforms"))}
              className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
            >
              Save
            </button>
          }
        >
          <div className="space-y-4">
            {["meta", "google"].map((platform) => {
              const config = setting("adPlatforms")[platform] || {};
              const facility = summary?.metadata?.adPlatforms?.facilities?.find(
                (item) => item.platform === platform,
              );
              const today = new Date();
              const months = [0, 1, 2].map((offset) => {
                const date = new Date(
                  today.getFullYear(),
                  today.getMonth() + offset,
                  1,
                );
                return {
                  key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
                  label: `${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`,
                };
              });
              return (
                <div
                  key={platform}
                  className="rounded border border-gray-100 p-3"
                >
                  <div className="mb-3 flex flex-wrap items-end gap-3">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        checked={config.enabled !== false}
                        onChange={(event) =>
                          updateAdPlatform(
                            platform,
                            "enabled",
                            event.target.checked,
                          )
                        }
                      />
                      {platform === "meta" ? "Meta" : "Google"} Ads enabled
                    </label>
                    <Field label="Day of month">
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={config.dayOfMonth || 28}
                        onChange={(event) =>
                          updateAdPlatform(
                            platform,
                            "dayOfMonth",
                            Number(event.target.value),
                          )
                        }
                        className="h-8 w-24 rounded border-gray-200 text-right"
                      />
                    </Field>
                    <Field label="Business day">
                      <select
                        value={config.businessDayRule || "previous"}
                        onChange={(event) =>
                          updateAdPlatform(
                            platform,
                            "businessDayRule",
                            event.target.value,
                          )
                        }
                        className="h-8 rounded border-gray-200 text-sm"
                      >
                        <option value="previous">Previous</option>
                        <option value="next">Next</option>
                        <option value="none">None</option>
                      </select>
                    </Field>
                    <p className="text-xs text-gray-500">
                      Baseline: {money(facility?.amountDue)}
                      {facility?.vendorName ? ` · ${facility.vendorName}` : ""}
                    </p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {months.map((month) => (
                      <Field key={month.key} label={`${month.label} override`}>
                        <CurrencyInput
                          value={config.overrides?.[month.key] ?? ""}
                          onChange={(value) =>
                            updateAdOverride(
                              platform,
                              month.key,
                              value,
                            )
                          }
                          className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                        />
                      </Field>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}
      {tab === "mappings" && (
        <Panel title="Category mappings">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              {(rules || []).filter((rule) => rule.isActive !== false).length}{" "}
              active mappings
            </p>
            <input
              value={mappingSearch}
              onChange={(event) => setMappingSearch(event.target.value)}
              placeholder="Search pattern or category"
              className="h-8 w-full max-w-xs rounded border-gray-200 text-sm"
            />
          </div>
          <div className="mb-3 grid gap-2 md:grid-cols-[2fr_1fr_120px_auto]">
            <Field label="Pattern">
              <input
                value={ruleForm.pattern}
                onChange={(event) =>
                  setRuleForm({ ...ruleForm, pattern: event.target.value })
                }
                className="h-8 w-full rounded border-gray-200 text-sm"
              />
            </Field>
            <Field label="Category">
              <select
                value={ruleForm.category}
                onChange={(event) =>
                  setRuleForm({ ...ruleForm, category: event.target.value })
                }
                className="h-8 w-full rounded border-gray-200 text-sm"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </Field>
            <button
              type="button"
              onClick={addRule}
              disabled={!ruleForm.pattern.trim()}
              className="h-8 self-end rounded bg-black px-3 text-xs font-medium text-white disabled:opacity-40"
            >
              {ruleForm.id ? "Save rule" : "Add rule"}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[650px] w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="px-2 py-2">Pattern</th>
                  <th className="px-2 py-2">Category</th>
                  <th className="px-2 py-2">Priority</th>
                  <th className="px-2 py-2">Active</th>
                  <th className="px-2 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(rules || [])
                  .filter((rule) =>
                    `${rule.pattern} ${rule.category}`
                      .toLowerCase()
                      .includes(mappingSearch.toLowerCase()),
                  )
                  .map((rule) => (
                    <tr key={rule.id} className="border-t border-gray-100">
                      <td className="px-2 py-2">{rule.pattern}</td>
                      <td className="px-2 py-2">{rule.category}</td>
                      <td className="px-2 py-2">{rule.priority}</td>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={rule.isActive !== false}
                          onChange={(event) =>
                            mutate(
                              () =>
                                thirteenWeekCashFlowApi.updateCategoryRule({
                                  ...rule,
                                  id: rule.id,
                                  isActive: event.target.checked,
                                }),
                              "Category mapping updated",
                            )
                          }
                        />
                      </td>
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => setRuleForm({ ...rule })}
                          className="mr-2 text-xs text-gray-600"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            mutate(
                              () =>
                                thirteenWeekCashFlowApi.deleteCategoryRule(
                                  rule.id,
                                ),
                              "Category rule deleted",
                            )
                          }
                          className="text-xs text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {!rules?.length && (
            <Empty text="No category rules have been added." />
          )}
          <div className="mt-4 border-t border-gray-100 pt-3">
            <button
              type="button"
              onClick={() => setShowBuiltin((current) => !current)}
              className="text-sm font-medium text-gray-700"
            >
              {showBuiltin ? "Hide" : "Show"} Built-in mappings (read-only)
            </button>
            {showBuiltin && (
              <div className="mt-2 space-y-1 text-xs text-gray-500">
                {builtinMappings.map((mapping) => (
                  <div key={`${mapping.pattern}-${mapping.category}`}>
                    {mapping.pattern} → {mapping.category}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Panel>
      )}
      {tab === "poAp" && (
        <Panel title="PO/AP due import">
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Mode">
              <select
                value={setting("apInventory").mode || "manual"}
                onChange={(event) =>
                  save("apInventory", {
                    ...setting("apInventory"),
                    mode: event.target.value,
                  })
                }
                className="h-8 w-full rounded-md border-gray-200"
              >
                <option value="manual">Manual schedules</option>
                <option value="po_auto">PO auto</option>
              </select>
            </Field>
            <Field label="Auto weekday">
              <select
                value={setting("apInventory").autoWeekday || 5}
                onChange={(event) =>
                  save("apInventory", {
                    ...setting("apInventory"),
                    autoWeekday: Number(event.target.value),
                  })
                }
                className="h-8 w-full rounded-md border-gray-200"
              >
                {weekdays.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
          </div>
        </Panel>
      )}
      <div hidden={tab !== "tax"}>
        <Panel
          title="Sales-tax schedules"
          action={
            <button
              type="button"
              onClick={() =>
                setTaxDraftRows((rows) => [
                  ...rows,
                  {
                    id: `new-${Date.now()}`,
                    category: "sales_tax",
                    amount: "",
                    frequency: "monthly",
                    dayOfMonth: "",
                    businessDayRule: "previous",
                    isActive: true,
                    meta: { state: "" },
                  },
                ])
              }
              className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
            >
              Add state
            </button>
          }
        >
          {dirtyTaxRowIds.size > 0 && (
            <p
              role="status"
              className="mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {dirtyTaxRowIds.size} sales-tax row
              {dirtyTaxRowIds.size === 1 ? " has" : "s have"} unsaved changes.
              {" "}Click Save on each row.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px] text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="px-2 py-2">State</th>
                  <th className="px-2 py-2">Amount</th>
                  <th className="px-2 py-2">Frequency</th>
                  <th className="px-2 py-2">Next due</th>
                  <th className="px-2 py-2">Due day</th>
                  <th className="px-2 py-2">Business-day rule</th>
                  <th className="px-2 py-2">Active</th>
                  <th className="px-2 py-2" />
                </tr>
              </thead>
              <tbody>
                {[
                  ...(schedules || []).filter((row) => row.category === "sales_tax"),
                  ...taxDraftRows,
                ]
                  .sort((left, right) =>
                    String(left.meta?.state || left.meta?.jurisdiction || "").localeCompare(
                      String(right.meta?.state || right.meta?.jurisdiction || ""),
                    ),
                  )
                  .map((row) => (
                    <TaxRowEditor
                      key={row.id}
                      initial={row}
                      onSave={saveTaxRow}
                      onDelete={deleteTaxRow}
                      onDirtyChange={onTaxDirtyChange}
                    />
                  ))}
              </tbody>
            </table>
          </div>
          {!schedules?.some((row) => row.category === "sales_tax") &&
            !taxDraftRows.length && (
              <Empty text="No sales-tax schedules are configured." />
            )}
        </Panel>
      </div>
      {tab === "holidays" && (
        <Panel title="Bank holidays">
          <div className="mb-3 grid gap-2 md:grid-cols-[1fr_2fr_auto]">
            <Field label="Date">
              <input
                type="date"
                value={holidayForm.date}
                onChange={(event) =>
                  setHolidayForm({ ...holidayForm, date: event.target.value })
                }
                className="h-8 w-full rounded border-gray-200 text-sm"
              />
            </Field>
            <Field label="Label">
              <input
                value={holidayForm.label}
                onChange={(event) =>
                  setHolidayForm({ ...holidayForm, label: event.target.value })
                }
                className="h-8 w-full rounded border-gray-200 text-sm"
              />
            </Field>
            <button
              type="button"
              onClick={saveHoliday}
              disabled={!holidayForm.date || !holidayForm.label}
              className="h-8 self-end rounded bg-black px-3 text-xs font-medium text-white disabled:opacity-40"
            >
              Add holiday
            </button>
          </div>
          {holidayError && (
            <p className="mb-2 text-xs text-red-700">{holidayError}</p>
          )}
          {(holidays || []).map((holiday) => (
            <div
              key={holiday.id || holiday.date}
              className="flex items-center justify-between border-b border-gray-100 py-2 text-sm"
            >
              <span>
                {dateLabel(holiday.date)} · {holiday.label || "Bank holiday"}
              </span>
              {holiday.id && (
                <button
                  type="button"
                  onClick={() =>
                    mutate(
                      () => thirteenWeekCashFlowApi.deleteHoliday(holiday.id),
                      "Holiday deleted",
                    )
                  }
                  className="text-xs text-red-700"
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </Panel>
      )}
      {tab === "payroll" && (
        <Panel title="Recurring cash">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">Payroll</p>
              <Field label="Amount">
                <CurrencyInput
                  value={setting("payroll").amount || 0}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      payroll: {
                        ...setting("payroll"),
                        amount: value,
                      },
                    }))
                  }
                  onBlur={() => save("payroll", setting("payroll"))}
                  className="h-8 w-full rounded border-gray-200 text-right"
                />
              </Field>
              <Field label="Frequency">
                <select
                  value="semi_monthly"
                  disabled
                  className="h-8 w-full rounded border-gray-200"
                >
                  <option value="semi_monthly">Semi-monthly</option>
                </select>
              </Field>
              <Field label="Business day">
                <select
                  value={setting("payroll").businessDayRule || "previous"}
                  onChange={(event) =>
                    save("payroll", {
                      ...setting("payroll"),
                      businessDayRule: event.target.value,
                    })
                  }
                  className="h-8 w-full rounded border-gray-200"
                >
                  <option value="previous">Previous</option>
                  <option value="next">Next</option>
                </select>
              </Field>
              <Field label="Lead days">
                <input
                  type="number"
                  min="0"
                  value={setting("payroll").leadDays ?? 1}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      payroll: {
                        ...setting("payroll"),
                        leadDays:
                          event.target.value === ""
                            ? 0
                            : Number(event.target.value),
                      },
                    }))
                  }
                  onBlur={() => save("payroll", setting("payroll"))}
                  className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                />
              </Field>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">
                AP operating
              </p>
              <Field label="Weekly amount">
                <CurrencyInput
                  value={setting("apOperating").weeklyAmount || 0}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      apOperating: {
                        ...setting("apOperating"),
                        weeklyAmount: value,
                      },
                    }))
                  }
                  onBlur={() => save("apOperating", setting("apOperating"))}
                  className="h-8 w-full rounded border-gray-200 text-right"
                />
              </Field>
              <Field label="Weekday">
                <select
                  value={setting("apOperating").weekday || 5}
                  onChange={(event) =>
                    save("apOperating", {
                      ...setting("apOperating"),
                      weekday: Number(event.target.value),
                    })
                  }
                  className="h-8 w-full rounded border-gray-200"
                >
                  {weekdays.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700">
                Owner distribution
              </p>
              <Field label="Amount">
                <CurrencyInput
                  value={setting("ownerDistribution").amount || 0}
                  onChange={(value) =>
                    setDraft((current) => ({
                      ...current,
                      ownerDistribution: {
                        ...setting("ownerDistribution"),
                        amount: value,
                      },
                    }))
                  }
                  onBlur={() =>
                    save("ownerDistribution", setting("ownerDistribution"))
                  }
                  className="h-8 w-full rounded border-gray-200 text-right"
                />
              </Field>
              <Field label="Day of month">
                <select
                  value={setting("ownerDistribution").dayOfMonth || 26}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      ownerDistribution: {
                        ...setting("ownerDistribution"),
                        dayOfMonth:
                          event.target.value === "last"
                            ? "last"
                            : Number(event.target.value),
                      },
                    }))
                  }
                  onBlur={() =>
                    save("ownerDistribution", setting("ownerDistribution"))
                  }
                  className="h-8 w-full rounded border-gray-200"
                >
                  {Array.from({ length: 31 }, (_, index) => index + 1).map(
                    (day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ),
                  )}
                  <option value="last">Last day</option>
                </select>
              </Field>
              <Field label="Business day">
                <select
                  value={
                    setting("ownerDistribution").businessDayRule || "previous"
                  }
                  onChange={(event) =>
                    save("ownerDistribution", {
                      ...setting("ownerDistribution"),
                      businessDayRule: event.target.value,
                    })
                  }
                  className="h-8 w-full rounded border-gray-200"
                >
                  <option value="previous">Previous</option>
                  <option value="next">Next</option>
                </select>
              </Field>
              <Field label="In AP recommendation">
                <select
                  value={
                    setting("ownerDistribution").requiredForAp === false
                      ? "deferred"
                      : "required"
                  }
                  onChange={(event) =>
                    save("ownerDistribution", {
                      ...setting("ownerDistribution"),
                      requiredForAp: event.target.value === "required",
                    })
                  }
                  className="h-8 w-full rounded border-gray-200"
                >
                  <option value="required">Required (hold back from AP)</option>
                  <option value="deferred">Deferred (don't hold back)</option>
                </select>
              </Field>
            </div>
          </div>
        </Panel>
      )}
      {tab === "cards" && (
        <Panel title="Plaid credit cards">
          <div className="mb-3 max-w-xs">
            <Field label="Default lead before due (used when no withdrawal day set)">
              <input
                type="number"
                min="0"
                value={setting("creditCards").debitLeadDays ?? 7}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    creditCards: {
                      ...setting("creditCards"),
                      debitLeadDays:
                        event.target.value === ""
                          ? 0
                          : Number(event.target.value),
                    },
                  }))
                }
                onBlur={() => save("creditCards", setting("creditCards"))}
                className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
              />
            </Field>
            <p className="mt-1 text-xs text-gray-500">
              Withdrawal day overrides the derived due-day timing; Flex defaults
              to day 20.
            </p>
          </div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">
              Active cards
            </p>
            {inactiveCards.length > 0 && (
              <button
                type="button"
                onClick={() => setShowInactiveCards((value) => !value)}
                className="text-xs text-gray-600"
              >
                {showInactiveCards ? "Hide" : "Show"} Not in use ({inactiveCards.length})
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="px-2 py-2">Card</th>
                  <th className="px-2 py-2 text-right">Statement</th>
                  <th className="px-2 py-2 text-right">Last payment</th>
                  <th className="px-2 py-2">Due day</th>
                  <th className="px-2 py-2">Override amount</th>
                  <th className="px-2 py-2">Payment/Withdrawal day</th>
                  <th className="px-2 py-2">Business day</th>
                  <th className="px-2 py-2">Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {[...activeCards, ...(showInactiveCards ? inactiveCards : [])]
                  .map((card) => {
                  const override =
                    setting("creditCards").overrides?.[card.plaidAccountId] ||
                    {};
                  return (
                    <React.Fragment key={card.plaidAccountId}>
                      <tr className="border-t border-gray-100">
                        <td className="px-2 py-2">
                          {card.customName || card.name}{" "}
                          {card.mask ? `···${card.mask}` : ""}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {money(card.statementBalance)}
                        </td>
                        <td className="px-2 py-2 text-right tabular-nums">
                          {money(
                            card.lastPaymentAmount ?? card.bankPayment?.amount,
                          )}
                          {card.lastPaymentAmount == null &&
                            card.bankPayment && (
                              <span className="block text-[11px] text-gray-500">
                                from bank {dateLabel(card.bankPayment.date)}
                              </span>
                            )}
                        </td>
                        <td className="px-2 py-2">
                          <span className="block">
                            {card.effectiveDueDay || "—"}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {card.dueDaySource || "plaid"}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <CurrencyInput
                            value={override.amount ?? ""}
                            onChange={(value) =>
                              updateCardOverride(
                                card,
                                "amount",
                                value,
                              )
                            }
                            className="h-8 w-28 rounded border-gray-200 text-right"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min="1"
                            max="31"
                            placeholder={String(card.withdrawalDay || "")}
                            value={override.withdrawalDay ?? ""}
                            onChange={(event) =>
                              updateCardOverride(
                                card,
                                "withdrawalDay",
                                event.target.value === ""
                                  ? null
                                  : Number(event.target.value),
                              )
                            }
                            className="h-8 w-28 rounded border-gray-200 text-right"
                          />
                          <span className="mt-1 block text-[11px] text-gray-500">
                            Derived {card.withdrawalDay || "—"}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <select
                            value={
                              override.businessDayRule ??
                              (card.isFlex
                                ? "none"
                                : setting("creditCards").businessDayRule ||
                                  "next")
                            }
                            onChange={(event) =>
                              updateCardOverride(
                                card,
                                "businessDayRule",
                                event.target.value,
                              )
                            }
                            className="h-8 rounded border-gray-200 text-xs"
                          >
                            <option value="next">Next</option>
                            <option value="previous">Previous</option>
                            <option value="none">None</option>
                          </select>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={override.enabled !== false}
                            onChange={(event) =>
                              updateCardOverride(
                                card,
                                "enabled",
                                event.target.checked,
                              )
                            }
                          />
                        </td>
                        <td className="px-2 py-2">
                          <button
                            type="button"
                            onClick={() =>
                              save("creditCards", setting("creditCards"))
                            }
                            className="text-xs font-medium text-gray-700"
                          >
                            Save
                          </button>
                        </td>
                      </tr>
                      <tr className="border-t border-gray-100">
                        <td
                          colSpan={9}
                          className="px-2 py-2 text-xs text-gray-500"
                        >
                          {card.isFlex && (
                            <span className="mr-2 rounded bg-blue-50 px-1.5 py-0.5 text-blue-700">
                              Flex: fixed on the 20th
                            </span>
                          )}
                          <span className="mr-2">Upcoming debits:</span>
                          {(card.upcoming || []).map((upcoming) => (
                            <label
                              key={upcoming.month}
                              className="mr-3 inline-flex items-center gap-1"
                            >
                              {monthLabel(upcoming.month)} (
                              {dateLabel(upcoming.scheduledDate)})
                              <input
                                type="date"
                                value={
                                  override.dates?.[upcoming.month] ||
                                  upcoming.manualDate ||
                                  ""
                                }
                                onChange={(event) =>
                                  updateCardDate(
                                    card,
                                    upcoming.month,
                                    event.target.value,
                                  )
                                }
                                className="h-7 rounded border-gray-200 text-xs"
                              />
                            </label>
                          ))}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                  })}
                {!cards?.length && (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-2 py-4 text-center text-sm text-gray-600"
                    >
                      No active Plaid credit cards were found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      {tab === "recurring" && (
        <Panel
          title="Recurring cash"
          action={
            <button
              type="button"
              onClick={() => setScheduleForm({})}
              className="h-8 rounded bg-black px-3 text-xs font-medium text-white"
            >
              Add schedule
            </button>
          }
        >
          {scheduleForm && scheduleForm.category !== "sales_tax" && (
            <ScheduleForm
              key={scheduleForm.id ?? "new"}
              initial={scheduleForm}
              onSave={saveSchedule}
              onCancel={() => setScheduleForm(null)}
            />
          )}
          {(schedules || [])
            .filter((row) => row.category !== "sales_tax")
            .map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between border-b border-gray-100 py-2 text-sm"
              >
                <span>
                  {row.label || row.category} · {money(row.amount)} ·{" "}
                  {row.frequency}
                </span>
                <span className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setScheduleForm(row)}
                    className="text-xs text-gray-600"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      mutate(
                        () => thirteenWeekCashFlowApi.deleteSchedule(row.id),
                        "Schedule deleted",
                      )
                    }
                    className="text-xs text-red-700"
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          {!schedules?.filter((row) => row.category !== "sales_tax").length &&
            !scheduleForm && (
              <Empty text="No recurring cash schedules are configured." />
            )}
        </Panel>
      )}
      {tab === "calendar" && (
        <Panel title="Revenue planning and deposit timing">
          <div className="grid gap-3 md:grid-cols-2">
            <p className="text-sm text-gray-600">
              Timezone: {setting("calendar").timezone || "America/Denver"} ·
              Horizon: {setting("calendar").horizonDays || 91} days
            </p>
            <div />
            <Field label="Revenue plan source">
              <select
                value={revenue.targetSource || "budget"}
                onChange={(event) =>
                  updateRevenue("targetSource", event.target.value)
                }
                className="h-8 w-full rounded border-gray-200 text-sm"
              >
                <option value="budget">Published budget</option>
                <option value="forecast_planning">Forecast Planning</option>
              </select>
            </Field>
            <Field label="Deposit model">
              <select
                value={revenue.distributionMode || "weekday_weights"}
                onChange={(event) =>
                  updateRevenue("distributionMode", event.target.value)
                }
                className="h-8 w-full rounded border-gray-200 text-sm"
              >
                <option value="weekday_weights">
                  Weekday weights, workbook
                </option>
                <option value="settlement_lag">
                  Sale date plus settlement lag
                </option>
              </select>
            </Field>
            <Field label="Shopify mix (%)">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={Number(revenue.channelMix?.shopify || 0) * 100}
                onChange={(event) => {
                  const shopify =
                    Math.max(
                      0,
                      Math.min(100, Number(event.target.value || 0)),
                    ) / 100;
                  updateRevenue("channelMix", {
                    ...(revenue.channelMix || {}),
                    shopify,
                    amazon: 1 - shopify,
                  });
                }}
                className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
              />
            </Field>
            <Field label="Amazon mix (%)">
              <input
                readOnly
                value={(Number(revenue.channelMix?.amazon || 0) * 100).toFixed(
                  1,
                )}
                className="h-8 w-full rounded border-gray-200 bg-gray-50 text-right tabular-nums"
              />
            </Field>
            {revenue.distributionMode !== "settlement_lag" && (
              <>
                {[
                  ["mon", "Monday"],
                  ["tue", "Tuesday"],
                  ["wed", "Wednesday"],
                  ["thu", "Thursday"],
                  ["fri", "Friday"],
                ].map(([key, label]) => (
                  <Field key={key} label={`${label} weight`}>
                    <input
                      type="number"
                      min="0"
                      step="0.05"
                      value={weekdayWeights[key] ?? 0}
                      onChange={(event) =>
                        updateRevenue("weekdayWeights", {
                          ...weekdayWeights,
                          [key]: Number(event.target.value),
                        })
                      }
                      className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                    />
                  </Field>
                ))}
                <div className="text-sm text-gray-600">
                  <p>
                    Weekday weight sum:{" "}
                    <span className="font-semibold tabular-nums">
                      {weekdayWeightSum.toFixed(2)}
                    </span>
                  </p>
                  {Math.abs(weekdayWeightSum - 5) > 0.001 && (
                    <p className="mt-1 text-xs text-amber-700">
                      Expected a weekday weight sum of 5.00.
                    </p>
                  )}
                </div>
                <Field label="Holiday Tuesday weight">
                  <input
                    type="number"
                    min="0"
                    step="0.05"
                    value={revenue.holidayTuesdayWeight ?? 2.55}
                    onChange={(event) =>
                      updateRevenue(
                        "holidayTuesdayWeight",
                        Number(event.target.value),
                      )
                    }
                    className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                  />
                </Field>
                <Field label="Workdays per month">
                  <input
                    type="number"
                    min="1"
                    value={revenue.workdaysPerMonth ?? 22}
                    onChange={(event) =>
                      updateRevenue(
                        "workdaysPerMonth",
                        Number(event.target.value),
                      )
                    }
                    className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                  />
                </Field>
                <Field label="Trailing actual weeks">
                  <input
                    type="number"
                    min="0"
                    value={revenue.trailingAvgWeeks ?? 4}
                    onChange={(event) =>
                      updateRevenue(
                        "trailingAvgWeeks",
                        Number(event.target.value),
                      )
                    }
                    className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                  />
                </Field>
                <Field label="Trailing actual days">
                  <input
                    type="number"
                    min="1"
                    value={revenue.trailingAvgDays ?? 14}
                    onChange={(event) =>
                      updateRevenue(
                        "trailingAvgDays",
                        Number(event.target.value),
                      )
                    }
                    className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
                  />
                </Field>
              </>
            )}
            {revenue.distributionMode === "settlement_lag" && (
              <>
            <Field label="Shopify settlement business days">
              <input
                type="number"
                min="0"
                value={setting("revenue").settlementDays?.shopify ?? 2}
                onChange={(event) =>
                  updateRevenue("settlementDays", {
                    ...setting("revenue").settlementDays,
                    shopify: Number(event.target.value),
                  })
                }
                className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
              />
            </Field>
            <Field label="Amazon settlement business days">
              <input
                type="number"
                min="0"
                value={setting("revenue").settlementDays?.amazon ?? 10}
                onChange={(event) =>
                  updateRevenue("settlementDays", {
                    ...setting("revenue").settlementDays,
                    amazon: Number(event.target.value),
                  })
                }
                className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
              />
            </Field>
            <Field label="Sale lookback days">
              <input
                type="number"
                min="1"
                value={setting("revenue").saleLookbackDays ?? 30}
                onChange={(event) =>
                  updateRevenue("saleLookbackDays", Number(event.target.value))
                }
                className="h-8 w-full rounded border-gray-200 text-right tabular-nums"
              />
            </Field>
              </>
            )}
          </div>
          {calendarError && (
            <p className="mt-2 text-xs text-red-700">{calendarError}</p>
          )}
          <button
            type="button"
            onClick={saveCalendar}
            className="mt-3 h-8 rounded bg-black px-3 text-xs font-medium text-white"
          >
            Save calendar
          </button>
        </Panel>
      )}
    </div>
  );
}
